import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch } from "../utils/api";

export const UserPreferencesContext = createContext();

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
  }
  return context;
};

export const UserPreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState({
    language: "en",
    timezone: "America/New_York"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  // Load user preferences on component mount
  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        // suppressUnauthorizedEvent: this fires on mount before the user may be
        // authenticated (e.g. during the Google OAuth callback flow).  A 401 here
        // must NOT trigger the global logout event that races with auth hydration.
        const response = await apiFetch("/api/auth/user-preferences/", {
          method: "GET",
          credentials: "include",
          suppressUnauthorizedEvent: true,
        });

        if (response.ok) {
          const data = await response.json();
          setPreferences(data);
        } else {
          console.error("Failed to load user preferences");
          setError("Failed to load preferences");
        }
      } catch (err) {
        console.error("Error loading user preferences:", err);
        setError("Network error loading preferences");
      } finally {
        setLoading(false);
      }
    };

    loadUserPreferences();
  }, []);

  // Load timezone options from backend
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        // suppressUnauthorizedEvent: same rationale as user-preferences above.
        const res = await apiFetch("/api/auth/timezones/", {
          credentials: "include",
          suppressUnauthorizedEvent: true,
        });
        if (res.ok) {
          const data = await res.json();
          setTimezoneOptions(Array.isArray(data.timezones) ? data.timezones : []);
        }
      } catch (e) {
        // Silently ignore; UI will fallback if needed
      }
    };
    loadTimezones();
  }, []);

  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      const response = await apiFetch("/api/auth/user-preferences/update/", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(newPreferences)
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.error || "Failed to update preferences" };
      }
    } catch (err) {
      console.error("Error updating user preferences:", err);
      return { success: false, error: "Network error" };
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    preferences,
    loading,
    error,
    timezoneOptions,
    updatePreferences
  }), [preferences, loading, error, timezoneOptions, updatePreferences]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export default UserPreferencesContext;
